/**
 * The plain-language composer of the simplified dashboard view (one text area, one send button, one reassurance
 * line). Demo page: /chats (composer at the bottom). The simplified *conversation* composer is the thread's own
 * (`ConversationChat`); this one serves the dashboard view, which has no thread to send into.
 */
import { SendHorizontal } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/Input.js';

interface Props {
  placeholder: string;
  onSend: (message: string) => void;
  disabled?: boolean;
  /** Hidden on the dashboard surface, where the nudge chips carry the note. */
  footerNote?: string;
  /** The runtime-policy control, shown beside the send hint. */
  policyControl?: React.ReactNode;
}

const MAX_TEXTAREA_PX = 160;

export const SimpleComposer: React.FC<Props> = ({
  placeholder,
  onSend,
  disabled,
  footerNote = 'The agent proposes; deployments and capital moves always need a second approver.',
  policyControl,
}) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_PX)}px`;
  };

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
    const el = textareaRef.current;
    if (el) el.style.height = 'auto';
  };

  return (
    <footer className="px-7 pb-4 pt-2.5">
      {/* Matches the stream's width above it, so the composer lines up with the messages. */}
      <div className="mx-auto w-full max-w-[1100px]">
        <div className="flex flex-col gap-2 rounded-xl border bg-surface px-3 py-2.5 focus-within:border-accent focus-within:ring-4 focus-within:ring-accent/20">
          <Textarea
            ref={textareaRef}
            rows={1}
            value={value}
            placeholder={placeholder}
            aria-label="Message"
            onChange={(e) => {
              setValue(e.target.value);
              autoGrow();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            className="max-h-40 min-h-6 border-0 bg-transparent px-0 py-0.5 shadow-none focus:ring-0"
          />
          <div className="flex items-center gap-2">
            {policyControl}
            <span className="text-xs text-faint">Enter to send · Shift+Enter for a new line</span>
            <span className="flex-1" />
            <Button iconOnly size="sm" aria-label="Send" onClick={submit} disabled={disabled || !value.trim()}>
              <SendHorizontal size={16} aria-hidden />
            </Button>
          </div>
        </div>
        {footerNote && <p className="mt-2 text-center text-xs text-muted">{footerNote}</p>}
      </div>
    </footer>
  );
};
